import { Component, OnDestroy } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { OnInit } from '@angular/core';
import { CoachService } from '../coach-service/coach-service.service';
import { AuthService } from 'src/app/authentication/auth/auth.service';
import { ToastService } from 'src/app/shared/services/toast-service/toast.service';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  debounceTime,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import { ToastComponent } from 'src/app/shared/components/toast/toast.component';
import { ModalService } from 'npx-family-happy-common';
import { ProvinceService } from 'src/app/shared/services/province-service/province.service';
import {
  CoachState,
  RouterStateManagerService,
} from 'src/app/shared/services/router-state-manager/router-state-manager.service';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Coach } from '../../../shared/models/coach.model';
import { CaregiverSelectionRecordSortable } from 'src/app/shared/models/caregiverSelectionRecordSortable.models';
import { CoachSummary } from '../../../shared/models/coach.model';
import { cellFormatter, emailFormatter } from 'src/app/shared/utils.functions';
import { AlertComponent } from 'src/app/shared/components/modals/alert/alert.component';

@Component({
  selector: 'app-coach-list',
  standalone: true,
  imports: [ReactiveFormsModule, RouterModule, CommonModule, AlertComponent],
  templateUrl: './coach-list.component.html',
  styleUrl: './coach-list.component.scss',
})
export class CoachListComponent implements OnInit, OnDestroy {
  // variables
  toast!: ToastComponent | null;
  filters: any = {};
  resetFiltersModal = false;
  isAdmin = false;
  private unsubscribe = new Subject<void>();
  coaches: any[] = [];
  private province = '';
  private coachState!: CoachState;
  private subscription!: Subscription;
  elementsPerPage = new UntypedFormControl(10);
  filtersRecreated = false;
  original: CoachSummary[] = [];
  filtersObserver = new BehaviorSubject<any>(undefined);

  showAlert = false;
  showMore = false;
  showFilters = false;
  selectedRef = '';
  showPageButton = true;

  //MAP FILTERS
  sortableBB: CaregiverSelectionRecordSortable[] = [];
  address!: any;
  addressInput = new UntypedFormControl('');
  resetChildren = false;

  // FORM
  form = new UntypedFormGroup({
    doc_ref: new UntypedFormControl(''),
    name: new UntypedFormControl(''),
    email: new UntypedFormControl(''),
    phone: new UntypedFormControl(''),
    age_lower: new UntypedFormControl(null),
    age_upper: new UntypedFormControl(null),
    limit: new UntypedFormControl(10),
    page: new UntypedFormControl(0),
    life_coaching: new UntypedFormControl(false),
    coaching_aziendale: new UntypedFormControl(false),
    executive_coaching: new UntypedFormControl(false),
    coaching_di_leadership: new UntypedFormControl(false),
    leadership_coaching: new UntypedFormControl(false),
    mindfulness_coaching: new UntypedFormControl(false),
    relationship_coaching: new UntypedFormControl(false),
    spiritual_coaching: new UntypedFormControl(false),
    coaching_for_personal_growth: new UntypedFormControl(false),
    corporate_wellness_coaching: new UntypedFormControl(false),
    mental_coaching_per_sportivi_e_performance: new UntypedFormControl(false),
  });

  constructor(
    private coachService: CoachService,
    private provinceService: ProvinceService,
    private stateManager: RouterStateManagerService,
    private modalService: ModalService,
    private authService: AuthService,
    private toastService: ToastService
  ) {
    this.authService
      .getToken()
      .subscribe(
        (token) => (this.isAdmin = this.authService.isUserAdmin(token) ?? false)
      );
    this.toastService
      .getChildComponent()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((component) => (this.toast = component));
  }

  ngOnInit(): void {
    let subscriptions: any = {};
    subscriptions.province = this.provinceService.getProvince().pipe(
      catchError((error) => {
        return error;
      })
    );
    subscriptions.state = this.stateManager.getCoachState().pipe(
      catchError((error) => {
        return error;
      })
    );
    combineLatest(subscriptions)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((values: any) => {
        this.province = values.province;
        this.coachState = values.state;
        if (!this.coachState.filters.province) {
          this.coachState.filters.province = '';
        }
        this.coachState.filters.page = 0;
        if (Object.keys(this.coachState.filters).length > 0) {
          if (this.province !== this.coachState.filters.province) {
            this.filters = this.recreateFilterObject(
              this.coachState.filters,
              false
            );
          } else {
            this.filters = this.recreateFilterObject(
              this.coachState.filters,
              true
            );
          }
          this.coachState.filters.province;
          this.filtersObserver.next(this.filters);
          this.form.patchValue(
            {
              doc_ref: this.coachState.filters.doc_ref ?? '',
              name: this.coachState.filters.info?.name ?? '',
              email: this.coachState.filters.info?.email ?? '',
              phone: this.coachState.filters.info?.phone ?? '',
              age_lower: this.coachState.filters.age_lower ?? null,
              age_upper: this.coachState.filters.age_upper ?? null,
              page: 0,
            },
            { emitEvent: true }
          );
        }
        if (!this.subscription) {
          if (this.coachState.filters.province === '') {
            this.coachState.filters.province = null;
          }
          this.subscription = this.coachService
            .getCoach(this.coachState.filters, this.elementsPerPage.value)
            .subscribe((data: any) => {
              if (!this.filtersRecreated) {
                this.populateCoach(data);
              } else {
                this.filtersRecreated = false;
              }
            });
          this.form.valueChanges
            .pipe(debounceTime(300), takeUntil(this.unsubscribe))
            .subscribe((value) => {
              console.log(value);
              if (value.page > (this.coachState.filters.page ?? 0)) {
                try {
                  this.processFilters();
                  this.askMoreCoaches();
                } catch (err) {
                  this.modalService.showErrorMessage(
                    'Non è stato possibile caricare i nuovi dati aggiornati.'
                  );
                }
              } else {
                this.coaches = [];
                this.processFilters();
                this.page?.setValue(0, { emitEvent: false });
                try {
                  this.askMoreCoaches();
                } catch (err) {
                  this.modalService.showErrorMessage(
                    'Non è stato possibile caricare i nuovi dati aggiornati.'
                  );
                }
              }
            });
          this.elementsPerPage.valueChanges
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((value) => {
              this.coaches = [];
              this.coachService
                .getCoach(this.coachState.filters, value)
                .subscribe();
            });
        }
      });

    this.form.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unsubscribe))
      .subscribe((value) => {
        this.filters = {
          doc_ref: value.doc_ref ?? null,
          name: value.name ?? null,
          email: value.email ?? null,
          phone: value.phone ?? null,
          age_lower: value.age_lower ?? null,
          age_upper: value.age_upper ?? null,
          specialties: this.populateSpecialties() ?? null,
        };
      });
  }

  populateSpecialties() {
    let specialties: string[] = [];
    if (this.form.get('life_coaching')?.value) {
      specialties.push('life_coaching'.toUpperCase());
    }
    if (this.form.get('leadership_coaching')?.value) {
      specialties.push('leadership_coaching'.toUpperCase());
    }
    if (this.form.get('mindfulness_coaching')?.value) {
      specialties.push('mindfulness_coaching'.toUpperCase());
    }
    if (this.form.get('relationship_coaching')?.value) {
      specialties.push('relationship_coaching'.toUpperCase());
    }
    if (this.form.get('spiritual_coaching')?.value) {
      specialties.push('spiritual_coaching'.toUpperCase());
    }
    if (this.form.get('coaching_for_personal_growth')?.value) {
      specialties.push('coaching_for_personal_growth'.toUpperCase());
    }
    if (this.form.get('coaching_aziendale')?.value) {
      specialties.push('coaching_aziendale'.toUpperCase());
    }
    if (this.form.get('executive_coaching')?.value) {
      specialties.push('executive_coaching'.toUpperCase());
    }
    if (this.form.get('coaching_di_leadership')?.value) {
      specialties.push('coaching_di_leadership'.toUpperCase());
    }
    if (this.form.get('corporate_wellness_coaching')?.value) {
      specialties.push('corporate_wellness_coaching'.toUpperCase());
    }
    if (this.form.get('mental_coaching_per_sportivi_e_performance')?.value) {
      specialties.push(
        'mental_coaching_per_sportivi_e_performance'.toUpperCase()
      );
    }

    return specialties;
  }

  get specialties() {
    return this.form.get('specialties');
  }

  get page() {
    return this.form.get('page');
  }
  get doc_ref() {
    return this.form.get('doc_ref');
  }
  get name() {
    return this.form.get('name');
  }
  get email() {
    return this.form.get('email');
  }
  get phone() {
    return this.form.get('phone');
  }
  get age_lower() {
    return this.form.get('age_lower');
  }
  get age_upper() {
    return this.form.get('age_upper');
  }

  get limit() {
    return this.form.get('limit');
  }

  rowClick(Coach: Coach | undefined) {
    if (!Coach) {
      this.coachState.id = 'new';
    } else {
      this.coachState.id = Coach.docRef;
    }
    this.coachState.route = 'coach-detail/' + this.coachState.id;
    this.coachState.step = 0;
    this.coachState.coach = undefined;
    this.ngOnDestroy();
    this.stateManager.setCoachState(this.coachState);
  }

  copyToClipboard(event: any) {
    navigator.clipboard.writeText(event);
    this.toastService.setAction(
      'Dati copiati con successo! Ora puoi incollarli dove vuoi.',
      true
    );
  }

  deleteCoach(doc_ref: string) {
    this.selectedRef = doc_ref;
    this.showAlert = true;
  }

  // delete alert confirmation
  onAlertConfimation(event: boolean) {
    if (event) {
      const index = this.coaches.findIndex(
        (item) => item.doc_ref === this.selectedRef
      );
      this.coaches.splice(index, 1);
      this.original.splice(index, 1);
      this.coachService
        .deleteCoach(this.selectedRef)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((res: any) => {
          this.modalService.outcomeMessage.next(
            'Coach eliminato con successo.'
          );
          this.modalService.outcomeType.next('Success');
          this.modalService.showOutcome.next(true);
        });
    }
    this.selectedRef = '';
    this.showAlert = false;
  }

  resetFilters() {
    this.coaches = [];
    this.resetFiltersModal = true;
    this.resetChildren = true;
    this.form.reset(
      {
        doc_ref: '',
        name: '',
        email: '',
        phone: '',
        specialties: '',
      },
      { emitEvent: false }
    );
    // Reset coach filter modal filters
    this.filters = undefined;
    this.filtersObserver.next(this.filters);
    this.askMoreCoaches(true);
  }

  processFilters(reset = false) {
    this.showFilters = false;
    let filters: any = {};
    if (reset) {
      filters = { page: 0 };
    } else {
      filters.page = this.page?.value;
      if (this.doc_ref?.value !== '') {
        filters.doc_ref = this.doc_ref?.value;
      }
      // 1. CHIAMATA E ASSEGNAZIONE CORRETTA
      const selectedSpecialties = this.populateSpecialties();

      // 2. CONTROLLO E INSERIMENTO NEL FILTRO
      if (selectedSpecialties.length > 0) {
        filters.specialties = selectedSpecialties;
      }
      if (this.name?.value !== '') {
        if (filters) {
          filters.name = this.name?.value;
        }
      }
      if (this.email?.value !== '') {
        if (filters) {
          let formattedMail = emailFormatter(this.email?.value);
          filters.email = formattedMail;
        }
      }
      if (this.phone?.value !== '') {
        if (filters) {
          let formattedPhone = cellFormatter(this.phone?.value);
          filters.phone = formattedPhone;
        }
      }
      if (this.age_lower?.value || this.age_upper?.value) {
        filters.age_lower = this.age_lower?.value;
        filters.age_upper = this.age_upper?.value;
      }
      filters.limit = this.limit?.value;
      if (this.filters) {
        if (this.filters.lang) {
          if (this.filters.lang.english) {
            filters.lang.english = true;
          }
          if (this.filters.lang.italian) {
            filters.lang.italian = true;
          }
          if (this.filters.lang.spanish) {
            filters.lang.spanish = true;
          }
          if (this.filters.lang.englishAdvanced) {
            filters.lang.englishAdvanced = true;
          }
          if (this.filters.lang.italianAdvanced) {
            filters.lang.italianAdvanced = true;
          }
          if (this.filters.lang.spanishAdvanced) {
            filters.lang.spanishAdvanced = true;
          }
        }
        if (this.filters.exp) {
          if (this.filters.exp.cluster1) {
            filters.exp.cluster1 = true;
          }
          if (this.filters.exp.cluster2) {
            filters.exp.cluster2 = true;
          }
        }
      }
      this.populateSpecialties();
      if (this.specialties?.value && this.specialties?.value.length > 0) {
        filters.specialties = this.specialties?.value;
      }
    }
    if (this.province !== '') {
      if (!filters || Object.keys(filters).length === 0) {
        filters = { province: this.province };
      } else {
        filters.province = this.province;
      }
    }
    return filters;
  }

  askMoreCoaches(resetFilters = false) {
    const filters = this.processFilters(resetFilters);
    try {
      this.coachState.filters = filters;
      this.coachService
        .getCoach(filters, this.elementsPerPage.value)
        .subscribe((coaches) => {
          if (coaches.length < this.elementsPerPage.value) {
            this.showPageButton = false;
          } else {
            this.showPageButton = true;
          }
        });
    } catch (err) {
      this.modalService.showErrorMessage(
        'Non è stato possibile caricare i nuovi dati aggiornati.'
      );
    }
  }

  generateCaregiverListSortable(bb: Coach[]) {
    this.sortableBB = [];
    bb.forEach((coach) => {
      let bbSortable = {
        fittingIndex: 0,
        record: {
          id: 0,
          requestID: 0,
          babysitter: null,
          petsitter: null,
          nurse: null,
          colf: null,
          nutritionist: null,
          psychologist: null,
          coach: coach,
          suitable: false,
          suitableDate: null,
          selected: false,
          selectedDate: null,
          chosen: false,
          chosenDate: null,
          discarded: false,
          discardedDate: null,
          discardedReason: '',
          distance: 0,
          favorite: false,
          cvSent: false,
          cvSentDate: null,
          contactSent: false,
          contactSentDate: null,
        },
      };
      this.sortableBB.push(bbSortable);
    });
  }

  private populateCoach(array: Coach[]) {
    if (array.length > 0) {
      this.generateCaregiverListSortable(array);
      this.original = array.map((item) => {
        return {
          name: item.info ? item.info.name : '-',
          surname: item.info ? item.info.surname : '-',
          email: item.info ? item.info.email : '-',
          phone: item.info ? item.info.phone : '-',
          docRef: item.docRef,
          cv: item.info.resumeHidden ?? '-',
          video: item.info.video,
          creationDate: new Date(),
          subscription: item.info.subscription,
          manualSubscription: item.info.manualSubscription,
          certificate: item.certificate,
        };
      });
    } else {
      this.generateCaregiverListSortable(array);
      this.original = [];
    }
    this.coaches.push(...this.original);
  }

  recreateFilterObject(filters: any, fromRequest: boolean) {
    if (filters) {
      if (fromRequest) {
        this.filtersRecreated = true;
      }
      return {
        page: filters.page ?? 0,
        minAge: this.age_lower?.value ?? null,
        maxAge: this.age_upper?.value ?? null,
        lang: {
          english: filters.lang ? filters.lang.english === true : false,
          englishAdvanced: filters.lang
            ? filters.lang.englishAdvanced === true
            : false,
          italian: filters.lang ? filters.lang.italian === true : false,
          italianAdvanced: filters.lang
            ? filters.lang.italianAdvanced === true
            : false,
          spanish: filters.lang ? filters.lang.spanish === true : false,
          spanishAdvanced: filters.lang
            ? filters.lang.spanishAdvanced === true
            : false,
        },
      };
    }
    return undefined;
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
